import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TaskListTemplateItemModel, TaskListTemplateItemRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['taskListTemplateItems']

type TaskListTemplateItemFilters = {
  task_list_template_id?: number,
  string?: string,
}

type UseTaskListTemplateItemsOptions = {
  filters?: TaskListTemplateItemFilters,
  performHttpRequests?: boolean,
  requestOptions?: TaskListTemplateItemRequestOptions,
}

function useTaskListTemplateItems(options: UseTaskListTemplateItemsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    task_list_template_id: filterTaskListTemplateId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { taskListTemplateItems: taskListTemplateItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { taskListTemplateItems } = useSelector(reduxState => reduxState.entities)

  const filteredTaskListTemplateItems = useMemo(() => {
    const filtered = Object.values(taskListTemplateItems).filter((taskListTemplateItem: TaskListTemplateItemModel) => {
      const { id, task_list_template_id, title } = taskListTemplateItem

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchTaskListTemplateId = matchFilterNumber(task_list_template_id, filterTaskListTemplateId)

      return matchString && matchTaskListTemplateId
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [taskListTemplateItemsUpdatedAt, JSON.stringify(filters)])

  const filteredTaskListTemplateItemsCount = filteredTaskListTemplateItems.length
  const hasFilteredTaskListTemplateItems = !!filteredTaskListTemplateItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTaskListTemplateItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingTaskListTemplateItems } = useReduxAction(
    'taskListTemplateItems',
    'loadTaskListTemplateItems',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTaskListTemplateItems,
    filteredTaskListTemplateItemsCount,
    hasFilteredTaskListTemplateItems,
    loading: loadingTaskListTemplateItems,
  }
}

export default useTaskListTemplateItems
